import type { Ref } from 'react'
import { observer } from 'mobx-react-lite'
import { AddCollaboratorsAsAdminDialogViewModel } from './AddCollaboratorsAsAdminDialogViewModel'
import {
  CustomSelect,
  type RenderSearchItemProps,
  SingleItemSelectedDefault,
} from '@/components/inputs/CustomSelect'
import { useCustomSelectStateFromItemSource } from '@/components/inputs/item-source-hook'
import { TextAreaContainer } from '@/components/inputs/TextAreaContainer'
import { SearchResultItem } from '@/components/inputs/SearchResultItem'
import { Avatar } from '@/components/Avatar'
import { type Member } from '@/features/iam/members/Member'
import {
  AddCollaboratorFields,
  AddCollaboratorProKindFilter,
  AddCollaboratorSearchItemDetails,
  AddCollaboratorsAsAdminDialog,
} from '@/features/jobs/details/components/AddCollaboratorsAsAdminDialog'

export interface AddCollaboratorsAsAdminDialogContainerProps {
  vm: AddCollaboratorsAsAdminDialogViewModel
}

export const AddCollaboratorsAsAdminDialogContainer = observer(
  function AddCollaboratorsAsAdminDialogContainer({
    vm,
  }: AddCollaboratorsAsAdminDialogContainerProps) {
    return (
      <AddCollaboratorsAsAdminDialog
        showing={vm.modal.showing}
        onClose={vm.modal.close}
        onSubmit={vm.submit}
        isSubmitPending={vm.submit.pending}
        isSubmitDisabled={vm.disabled}
        isSubmitRejected={vm.submit.rejected}
        submitError={vm.submit.error}
        isTypeSelectorVisible={!vm.isDraftJob && !vm.canOnlyAddClient}
        isAddingClient={vm.isAddingClient}
        isAddingPro={vm.isAddingPro}
        onAddingClient={vm.setAddingClient}
        onAddingPro={vm.setAddingPro}
      >
        {vm.isAddingClient && <AddClientFormContainer vm={vm} />}
        {vm.isAddingPro && !vm.canOnlyAddClient && (
          <AddProFormContainer vm={vm} />
        )}
      </AddCollaboratorsAsAdminDialog>
    )
  },
)

interface AddFormContainerProps {
  vm: AddCollaboratorsAsAdminDialogViewModel
}

const AddClientFormContainer = observer(function AddClientFormContainer({
  vm,
}: AddFormContainerProps) {
  const selectProps = useCustomSelectStateFromItemSource({
    itemSource: vm.isClientTeamJob
      ? vm.fetchTeamMembers
      : vm.fetchWorkspaceMembers,
  })

  const clientError = vm.validation.getError('collaborator')
  const reasonError = vm.validation.getError('reason')

  return (
    <AddCollaboratorFields
      label="Client"
      selectError={clientError}
      select={
        <CustomSelect
          {...selectProps}
          multi={false}
          icon="dropdown"
          iconColor="var(--grey600)"
          renderItem={(itemProps) => (
            <SingleItemSelectedDefault
              {...itemProps}
              item={itemProps}
              itemToString={(i) => String(i.item?.displayName)}
              visual={<Avatar size={24} subject={itemProps.item} />}
            />
          )}
          placeholder={
            vm.isClientTeamJob ? 'Search team members' : 'Search any available'
          }
          value={vm.collaborator}
          onChange={vm.setCollaborator}
          renderSearchItem={(props) => (
            <AddCollaboratorSearchItemContainer {...props} />
          )}
          testId="add-job-collaborator-as-admin-dropdown"
        />
      }
      reasonField={
        <TextAreaContainer
          rows={4}
          model={vm}
          field="reason"
          name="reason"
          maxLength={AddCollaboratorsAsAdminDialogViewModel.MAX_REASON_LENGTH}
          error={reasonError}
          status={reasonError ? 'danger' : 'default'}
          placeholder={vm.reasonHelpText}
          help={`${vm.reasonCharsRemaining} characters remaining`}
          data-test="add-job-collaborator-as-admin-reason-input"
        />
      }
    />
  )
})

const AddProFormContainer = observer(function AddProFormContainer({
  vm,
}: AddFormContainerProps) {
  const selectProps = useCustomSelectStateFromItemSource({
    itemSource: vm.canDisplayAllPros
      ? vm.fetchWorkspaceMembers
      : vm.fetchQualifiedPros,
  })

  const clientError = vm.validation.getError('collaborator')
  const reasonError = vm.validation.getError('reason')

  return (
    <>
      <AddCollaboratorProKindFilter
        isQualifiedOnly={vm.canDisplayQualifiedPros}
        isAnyAvailable={vm.canDisplayAllPros}
        onQualifiedOnly={vm.setDisplayQualifiedPros}
        onAnyAvailable={vm.setDisplayAllPros}
      />
      <AddCollaboratorFields
        label="Pro"
        selectError={clientError}
        selectHelp={vm.proSelectionHelpText}
        select={
          <CustomSelect
            {...selectProps}
            multi={false}
            icon="dropdown"
            iconColor="var(--grey600)"
            renderItem={(itemProps) => (
              <SingleItemSelectedDefault
                {...itemProps}
                item={itemProps}
                itemToString={(i) => String(i.item?.displayName)}
                visual={<Avatar size={24} subject={itemProps.item} />}
              />
            )}
            placeholder={
              vm.canDisplayAllPros
                ? 'Search any available'
                : 'Search qualified only'
            }
            value={vm.collaborator}
            onChange={vm.setCollaborator}
            renderSearchItem={(props) => (
              <AddCollaboratorSearchItemContainer {...props} />
            )}
            testId="add-job-collaborator-as-admin-dropdown"
          />
        }
        reasonField={
          vm.modalType !== 'requestingChampion' && (
            <TextAreaContainer
              rows={4}
              model={vm}
              field="reason"
              name="reason"
              maxLength={
                AddCollaboratorsAsAdminDialogViewModel.MAX_REASON_LENGTH
              }
              error={reasonError}
              status={reasonError ? 'danger' : 'default'}
              placeholder={vm.reasonHelpText}
              help={`${vm.reasonCharsRemaining} characters remaining`}
              data-test="add-job-collaborator-as-admin-reason-input"
            />
          )
        }
      />
    </>
  )
})

interface AddCollaboratorSearchItemContainerProps extends RenderSearchItemProps<Member | null> {
  ref?: Ref<HTMLDivElement>
}

const AddCollaboratorSearchItemContainer = observer(
  function AddCollaboratorSearchItemContainer({
    item,
    itemToString: _itemToString,
    ref,
    ...itemProps
  }: AddCollaboratorSearchItemContainerProps) {
    return (
      <SearchResultItem
        {...itemProps}
        ref={ref}
        visual={<Avatar size={24} subject={item} />}
        testId={
          'add-job-collaborator-as-admin--option-' +
          item?.user.publicId?.toLowerCase()
        }
      >
        <AddCollaboratorSearchItemDetails
          name={item?.displayName}
          email={item?.user.email}
        />
      </SearchResultItem>
    )
  },
)
